import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';
import { Chart, ChartConfiguration, ChartData, Plugin } from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ToastrService } from 'ngx-toastr';
import { Client, EnergyBufferDataOut, EnergyReadDataOut, LoadProfileCommandEvent, MeterAvailabilityDataIn, PageInfo } from '../../../../../api/api-reference';
import { PaginationComponent } from '../../../../core/components/pagination/pagination.component';
import { UnitConverter } from '../../../../../pipes/unit-conversion';
import { TableScrollService } from '../../../../../services/table-scroll.service';
import { cssVarRgba } from '../../../../../helper/css-var';

Chart.register(zoomPlugin);

// draws a dashed vertical line at the currently hovered x position, scoped to this chart only
const verticalHoverLinePlugin: Plugin<'line'> = {
  id: 'verticalHoverLine',
  afterDraw(chart: Chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = cssVarRgba('--page-title', 0.35);
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

// draws a permanent dashed vertical line at a reading whose values are ALL null, since it would
// otherwise be completely invisible on the chart (no line/point can be drawn through a null
// y-value) - lets the user see it's there and hover over it. Readings with at least one real
// value already show up via the normal line/point, so this only fires for the fully-empty ones
// (allNullFlags, set in buildChartData) - otherwise a dense range would draw one line per point
const realReadingMarkerPlugin: Plugin<'line'> = {
  id: 'realReadingMarker',
  afterDraw(chart: Chart) {
    const points = chart.getDatasetMeta(0)?.data;
    const allNullFlags = (chart.data as { allNullFlags?: boolean[] }).allNullFlags;
    if (!points?.length || !allNullFlags?.length) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = cssVarRgba('--page-title', 0.45);
    points.forEach((point, i) => {
      if (!allNullFlags[i]) return;
      ctx.moveTo(point.x, chartArea.top);
      ctx.lineTo(point.x, chartArea.bottom);
    });
    ctx.stroke();
    ctx.restore();
  },
};

// hidden dataset with a non-null y-value at every real reading, purely so Chart.js's index-mode
// hover always has a live point to anchor to - without it, a reading whose real series are ALL
// null would never trigger the tooltip at all (Chart.js's interaction skips null-valued points
// entirely, even to just determine "nearest index"). Filtered out of the rendered tooltip below.
const REAL_READING_MARKER_LABEL = '__realReadingMarker';

// draws just the two vertical edge lines of the current drag-zoom selection (chartjs-plugin-zoom's
// own drag box only supports a full 4-sided rectangle border, so its border is disabled and this
// plugin tracks the same drag independently to draw only the left/right edges instead)
const dragSelectLinesPlugin: Plugin<'line'> = {
  id: 'dragSelectLines',
  afterInit(chart: Chart) {
    const canvas = chart.canvas;
    let startX: number | undefined;
    let currentX: number | undefined;

    canvas.addEventListener('mousedown', (event) => {
      startX = currentX = getRelativePosition(event, chart).x;
    });
    canvas.addEventListener('mousemove', (event) => {
      if (startX === undefined) return;
      currentX = getRelativePosition(event, chart).x;
      chart.draw();
    });
    const clear = () => {
      if (startX === undefined) return;
      startX = currentX = undefined;
      chart.draw();
    };
    canvas.addEventListener('mouseup', clear);
    canvas.addEventListener('mouseleave', clear);

    (chart as unknown as { $dragSelectLines: () => { startX: number; currentX: number } | null }).$dragSelectLines =
      () => (startX !== undefined && currentX !== undefined ? { startX, currentX } : null);
  },
  afterDraw(chart: Chart) {
    const range = (chart as unknown as { $dragSelectLines?: () => { startX: number; currentX: number } | null }).$dragSelectLines?.();
    if (!range) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = cssVarRgba('--page-title', 0.5);
    ctx.lineWidth = 1;
    for (const x of [range.startX, range.currentX]) {
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    }
    ctx.restore();
  },
};

interface EnergyReading {
  meterTime: Date;
  activeEnergyImport: number | null;
  activeEnergyExport: number | null;
  reactiveEnergyImport: number | null;
  reactiveEnergyExport: number | null;
  apparentEnergyImport: number | null;
  apparentEnergyExport: number | null;
  absoluteActiveEnergy: number | null;
}

interface SeriesConfig {
  key: keyof EnergyReading;
  label: string;
  labelKey: string;
  color: string;
  unit: string;
}

type ViewMode = 'graph' | 'table';

@Component({
  selector: 'app-energy-reads',
  standalone: true,
  imports: [CommonModule, TranslateModule, NgChartsModule, PaginationComponent, UnitConverter],
  templateUrl: './energy-reads.component.html',
  styleUrl: './energy-reads.component.scss',
})
export class EnergyReadsComponent implements OnInit {
  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective<'line'>;

  // the table is only in the DOM while viewMode === 'table' (see the @if in the template), so a
  // setter is used instead of ngAfterViewInit - it fires each time the element (re)appears and
  // wires up drag/wheel scrolling on that fresh element, same behavior as scheduled-energy-details
  @ViewChild('tableContainer') set tableContainerRef(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) this.initializeTableEvents(ref.nativeElement);
  }

  @Input() meterId = '';

  ranges = ['1H', '8H', '1D', '3D', '7D', '1M'];
  activeRange = '1H';
  viewMode: ViewMode = 'graph';
  readings: EnergyReading[] = [];

  // Table view uses the paginated register endpoint (same one backing /scheduled/energy-details),
  // not the chart's readings - it can return far more rows than the chart wants to plot.
  tableRows: EnergyBufferDataOut[] = [];
  tableCurrentPage = 1;
  tablePageSize = 10;
  tableLastPageSize = 1;
  tableRowCount = 0;

  // true when the loaded readings span more than a day, so axis/tooltip labels need a date
  // to stay unambiguous (otherwise two points on different days can show the same time)
  private showDateOnAxis = false;

  seriesConfig: SeriesConfig[] = [
    { key: 'absoluteActiveEnergy', label: 'Absolute Energy', labelKey: 'ABSOLUTE_ENERGY', color: cssVarRgba('--chart-gray', 1), unit: 'WH' },
    { key: 'activeEnergyImport', label: 'Active Energy (Import)', labelKey: 'ACTIVE_ENERGY_IMPORT', color: cssVarRgba('--chart-green', 1), unit: 'WH' },
    { key: 'activeEnergyExport', label: 'Active Energy (Export)', labelKey: 'ACTIVE_ENERGY_EXPORT', color: cssVarRgba('--chart-red', 1), unit: 'WH' },
    { key: 'reactiveEnergyImport', label: 'Reactive Energy (Import)', labelKey: 'REACTIVE_ENERGY_IMPORT', color: cssVarRgba('--chart-pink', 1), unit: 'VARH' },
    { key: 'reactiveEnergyExport', label: 'Reactive Energy (Export)', labelKey: 'REACTIVE_ENERGY_EXPORT', color: cssVarRgba('--chart-teal', 1), unit: 'VARH' },
    { key: 'apparentEnergyImport', label: 'Apparent Energy (Import)', labelKey: 'APPARENT_ENERGY_IMPORT', color: cssVarRgba('--chart-purple', 1), unit: 'VAH' },
    { key: 'apparentEnergyExport', label: 'Apparent Energy (Export)', labelKey: 'APPARENT_ENERGY_EXPORT', color: cssVarRgba('--chart-orange', 1), unit: 'VAH' },
  ];

  seriesVisible: boolean[] = this.seriesConfig.map(() => true);

  chartPlugins = [realReadingMarkerPlugin, verticalHoverLinePlugin, dragSelectLinesPlugin];

  chartData: ChartData<'line', { x: number; y: number | null }[]> & { allNullFlags?: boolean[] } = { labels: [], datasets: [] };

  chartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        type: 'time',
        grid: { color: cssVarRgba('--page-title', 0.08) },
        // autoSkip (triggered by maxTicksLimit) can end up dropping the very first/last tick if
        // its spacing math doesn't land exactly on them - force the first and last reading's
        // label back in so they're always visible, since those are also the first/last points
        // drawn on the chart
        beforeFit: (scale) => {
          const ticks = scale.ticks;
          if (!ticks.length) return;
          const firstValue = scale.min;
          if (Number.isFinite(firstValue) && ticks[0].value !== firstValue) {
            ticks[0] = { value: firstValue, label: this.formatTime(new Date(firstValue), this.showDateOnAxis, true) };
          }
          const lastIndex = ticks.length - 1;
          const lastValue = scale.max;
          if (Number.isFinite(lastValue) && ticks[lastIndex].value !== lastValue) {
            ticks[lastIndex] = { value: lastValue, label: this.formatTime(new Date(lastValue), this.showDateOnAxis, true) };
          }
        },
        ticks: {
          color: cssVarRgba('--page-subtitle', 1),
          // cap how many labels show at once, regardless of how many readings came back,
          // so a dense range doesn't crowd the axis
          maxTicksLimit: 12,
          maxRotation: 0,
          // TimeScale calls this with the already-formatted label as the first arg, not the
          // raw timestamp - read the real timestamp back off the tick entry at this index
          callback: (_value, index, ticksArray) => {
            const time = ticksArray[index]?.value;
            return Number.isFinite(time) ? this.formatTime(new Date(time), this.showDateOnAxis, true) : '';
          },
        },
      },
      y: {
        min: undefined,
        max: undefined,
        grid: { color: cssVarRgba('--page-title', 0.08) },
        ticks: {
          color: cssVarRgba('--page-subtitle', 1),
          callback: (value) => new Intl.NumberFormat('en-US').format(value as number),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        // the hidden marker dataset only exists to keep the tooltip alive for all-null
        // readings (see REAL_READING_MARKER_LABEL above) - never show it as its own row
        filter: (item) => item.dataset.label !== REAL_READING_MARKER_LABEL,
        callbacks: {
          // items can be empty here even while the tooltip is showing (the marker dataset is
          // what triggered it, then got filtered out above) - fall back to the raw active
          // element's index in that case
          title: (items) => {
            const dataIndex = items[0]?.dataIndex ?? this.chartDirective?.chart?.getActiveElements()[0]?.index;
            return dataIndex === undefined ? '' : this.formatTime(this.readings[dataIndex].meterTime, true);
          },
          label: (context) => {
            const value = context.parsed.y;
            const unit = this.seriesConfig[context.datasetIndex]?.unit;
            const formatted = value === null ? 'N/A' : this.unitConverter.transform(value, unit);
            return `${context.dataset.label}: ${formatted}`;
          },
          // Chart.js's index-mode tooltip drops a dataset's row entirely when its value is
          // null at this index (rather than showing it with no value) - add it back for every
          // visible series that's missing, so a null reading still shows up as N/A
          afterBody: (items) => {
            const dataIndex = items[0]?.dataIndex ?? this.chartDirective?.chart?.getActiveElements()[0]?.index;
            if (dataIndex === undefined) return [];
            const shownLabels = new Set(items.map((i) => i.dataset.label));
            return this.seriesConfig
              .filter((s, i) => this.seriesVisible[i] && !shownLabels.has(s.label))
              .map((s) => `${s.label}: N/A`);
          },
        },
      },
      // click-drag-release over a region of the chart to zoom into it, adjusting both axes
      // to that region - matches the Azure Portal metrics chart behavior. mode is 'x' so the
      // drag selection always spans the chart's full height (not just the drag's own vertical
      // distance); the y-axis is then fitted to the zoomed-in data by onZoomComplete below.
      zoom: {
        zoom: {
          // border is off - dragSelectLinesPlugin draws just the left/right edge lines instead
          // of the plugin's own full 4-sided rectangle outline
          drag: {
            enabled: true,
            backgroundColor: cssVarRgba('--page-title', 0.12),
            borderWidth: 0,
    },
          mode: 'x',
          onZoomComplete: ({ chart }) => this.fitYAxisToVisibleRange(chart),
        },
      },
    },
  };

  get timezoneOffset(): string {
    const minutes = Number(localStorage.getItem('tz') ?? 0);
    const abs = Math.abs(minutes);
    const sign = minutes >= 0 ? '+' : '-';
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}${String(abs % 60).padStart(2, '0')}`;
  }

  private unitConverter = new UnitConverter();

  constructor(private client: Client, private toastr: ToastrService, private tableScrollService: TableScrollService) {}

  private initializeTableEvents(tableContainer: HTMLElement): void {
    tableContainer.addEventListener('mousedown', (event: MouseEvent) =>
      this.tableScrollService.startDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mousemove', (event: MouseEvent) =>
      this.tableScrollService.onDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mouseup', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    tableContainer.addEventListener('mouseleave', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    tableContainer.addEventListener('wheel', (event: WheelEvent) =>
      this.tableScrollService.handleHorizontalScroll(event, tableContainer),
    );
  }

  ngOnInit(): void {
    this.updateRangeData();
  }

  resetZoom() {
    this.chartDirective?.chart?.resetZoom();
  }

  selectRange(range: string) {
    this.activeRange = range;
    this.updateRangeData();
    if (this.viewMode === 'table') {
      this.loadTableRows(1);
    }
  }

  setViewMode(mode: ViewMode) {
    this.viewMode = mode;
    if (mode === 'table') {
      this.loadTableRows(1);
    }
  }

  toggleSeries(index: number) {
    this.seriesVisible[index] = !this.seriesVisible[index];
    this.chartDirective?.hideDataset(index, !this.seriesVisible[index]);
  }

  // recomputes the y-axis to fit only the data visible within the current (post-zoom)
  // x range, since drag-zoom mode is 'x' only and doesn't adjust y on its own. x is a
  // time scale, so min/max here are timestamps (ms), not reading indices.
  private fitYAxisToVisibleRange(chart: Chart): void {
    const { min: xMin, max: xMax } = chart.scales['x'];
    const visible = this.readings.filter((r) => {
      const t = r.meterTime.getTime();
      return t >= xMin && t <= xMax;
    });

    const yRange = this.computeYRange(visible);
    const yScale = chart.options.scales!['y'] as { min?: number; max?: number };
    yScale.min = yRange.min;
    yScale.max = yRange.max;

    chart.update('none');
  }

  private computeYRange(readings: EnergyReading[]): { min?: number; max?: number } {
    const allValues = readings
      .flatMap((r) => this.seriesConfig.map((s) => r[s.key] as number | null))
      .filter((v): v is number => v !== null);

    if (!allValues.length) return { min: undefined, max: undefined };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1 || 1;
    return { min: min - padding, max: max + padding };
  }

  private buildChartData(): void {
    const readings = this.readings;
    // a single point has no neighbor to draw a line to, so it'd otherwise be invisible
    // with pointRadius 0 - show it as a dot instead
    const pointRadius = readings.length === 1 ? 4 : 0;

    // these are cumulative registers moving in a narrow band (e.g. 15000-15750), so a
    // y-axis starting near 0 flattens out all the day/night variation - zoom into the
    // actual min/max (with a little padding) so the slope changes are visible
    const yScale = this.chartOptions!.scales!['y'] as { min?: number; max?: number };
    const yRange = this.computeYRange(readings);
    yScale.min = yRange.min;
    yScale.max = yRange.max;
    // the hidden marker dataset (see REAL_READING_MARKER_LABEL) needs a y-value that falls
    // inside the visible range, or Chart.js treats it as off-screen and ignores it for hover
    const markerY = yRange.min !== undefined && yRange.max !== undefined ? (yRange.min + yRange.max) / 2 : 0.5;

    this.chartOptions = { ...this.chartOptions! };

    this.chartData = {
      labels: [],
      datasets: [
        ...this.seriesConfig.map((s, i) => ({
          label: s.label,
          // time x-axis: each point is positioned by its real timestamp, so a gap in the
          // data shows up as a visual gap instead of being hidden by even index spacing
          data: readings.map((r) => ({ x: r.meterTime.getTime(), y: r[s.key] as number | null })),
          borderColor: s.color,
          backgroundColor: s.color,
          pointBackgroundColor: s.color,
          borderWidth: 2,
          pointRadius,
          tension: 0.3,
          spanGaps: true,
          hidden: !this.seriesVisible[i],
        })),
        {
          label: REAL_READING_MARKER_LABEL,
          data: readings.map((r) => ({ x: r.meterTime.getTime(), y: markerY })),
          borderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
      // consumed by realReadingMarkerPlugin - which readings have no real value in any series
      allNullFlags: readings.map((r) => this.seriesConfig.every((s) => r[s.key] === null)),
    };
  }

  private updateRangeData(): void {
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - this.rangeDurationMinutesFor(this.activeRange) * 60000);

    this.client
      .getEnergyReadForInterval(
        new MeterAvailabilityDataIn({
          meterId: this.meterId,
          startTime,
          endTime,
        })
      )
      .subscribe({
        next: (response) => {
          this.readings = (response ?? [])
            .map((item) => this.mapReading(item))
            .sort((a, b) => a.meterTime.getTime() - b.meterTime.getTime());

          const first = this.readings[0]?.meterTime.getTime();
          const last = this.readings[this.readings.length - 1]?.meterTime.getTime();
          this.showDateOnAxis = first !== undefined && last !== undefined && last - first > 24 * 60 * 60000;

          this.buildChartData();
          // a stale drag-zoom transform from before this reload would otherwise stay
          // applied on top of the newly loaded range
          this.resetZoom();
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
  }

  loadTableRows(page: number): void {
    this.tableCurrentPage = page;
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - this.rangeDurationMinutesFor(this.activeRange) * 60000);

    this.client
      .getProcessedEnergyRegisters(
        new LoadProfileCommandEvent({
          meterId: this.meterId,
          pageInfo: new PageInfo({ page, pageSize: this.tablePageSize }),
          startDate: startTime,
          endDate: endTime,
        })
      )
      .subscribe({
        next: (response) => {
          this.tableRows = response.data ?? [];
          this.tableRowCount = response.count ?? 0;
          this.tableLastPageSize = Math.max(Math.ceil(this.tableRowCount / this.tablePageSize), 1);
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
  }

  private mapReading(item: EnergyReadDataOut): EnergyReading {
    return {
      meterTime: item.meterTime,
      activeEnergyImport: item.activeEnergyImport ?? null,
      activeEnergyExport: item.activeEnergyExport ?? null,
      reactiveEnergyImport: item.reactiveEnergyImport ?? null,
      reactiveEnergyExport: item.reactiveEnergyExport ?? null,
      apparentEnergyImport: item.apparentEnergyImport ?? null,
      apparentEnergyExport: item.apparentEnergyExport ?? null,
      absoluteActiveEnergy: item.absoluteActiveEnergy ?? null,
    };
  }

  private rangeDurationMinutesFor(range: string): number {
    switch (range) {
      case '1H': return 60;
      case '8H': return 8 * 60;
      case '1D': return 24 * 60;
      case '3D': return 3 * 24 * 60;
      case '7D': return 7 * 24 * 60;
      case '1M': return this.daysInCurrentMonth() * 24 * 60;
      default: return 0;
    }
  }

  private daysInCurrentMonth(): number {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }

  // multiline splits date and time onto separate lines (used for the x-axis, which has room
  // to stack them); tooltip title keeps them on one line since it's meant to read like a sentence
  private formatTime(date: Date, includeDate = false, multiline = false): string | string[] {
    const offsetMs = Number(localStorage.getItem('tz') ?? 0) * 60000;
    const shifted = new Date(date.getTime() + offsetMs);
    const hours = shifted.getUTCHours();
    const minutes = shifted.getUTCMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    const time = `${displayHour}:${minutes} ${period}`;

    if (!includeDate) return time;

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const datePart = `${months[shifted.getUTCMonth()]} ${shifted.getUTCDate()}`;
    return multiline ? [datePart, time] : `${datePart} ${time}`;
  }
}
